import { useState } from 'react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    servicio: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5s8mmnc87p5i2kb2iig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          servicio: '',
          mensaje: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-20 bg-gradient-to-br from-white to-teal-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Solicita tu <strong>presupuesto gratuito</strong>
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Completa el formulario y te contactaremos en menos de 24 horas para ofrecerte un presupuesto personalizado sin compromiso.
            </p>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500/10 rounded-full flex-shrink-0">
                  <i className="ri-phone-line text-2xl text-teal-500"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Teléfono</h4>
                  <a href="tel:+34629482566" className="text-gray-600 hover:text-teal-500 transition-colors cursor-pointer">
                    629 482 566
                  </a>
                  <p className="text-sm text-gray-500 mt-1">Lunes a Sábado: 8:00 - 19:00</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-green-500/10 rounded-full flex-shrink-0">
                  <i className="ri-whatsapp-line text-2xl text-green-500"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">WhatsApp</h4>
                  <a 
                    href="https://wa.me/34629482566?text=Hola,%20me%20gustaría%20solicitar%20información%20sobre%20sus%20servicios%20de%20ayuda%20a%20domicilio." 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-600 hover:text-green-500 transition-colors cursor-pointer"
                  >
                    629 482 566
                  </a>
                  <p className="text-sm text-gray-500 mt-1">Respuesta inmediata</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500/10 rounded-full flex-shrink-0">
                  <i className="ri-mail-line text-2xl text-teal-500"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Email</h4>
                  <a href="mailto:info@ayudadomiciliogc.es" className="text-gray-600 hover:text-teal-500 transition-colors cursor-pointer">
                    info@ayudadomiciliogc.es
                  </a>
                  <p className="text-sm text-gray-500 mt-1">Respuesta en 24 horas</p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-500/10 rounded-full flex-shrink-0">
                  <i className="ri-map-pin-line text-2xl text-teal-500"></i>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">Zona de servicio</h4>
                  <p className="text-gray-600">Toda la isla de Gran Canaria</p>
                  <p className="text-gray-600">Las Palmas, Telde, Maspalomas y más</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form id="contact_form" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-5">
                <div>
                  <label htmlFor="nombre" className="block text-sm font-semibold text-gray-900 mb-2">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    id="nombre"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="Tu nombre"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="telefono" className="block text-sm font-semibold text-gray-900 mb-2">
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                    placeholder="+34 600 000 000"
                  />
                </div>

                <div>
                  <label htmlFor="servicio" className="block text-sm font-semibold text-gray-900 mb-2">
                    Servicio de interés *
                  </label>
                  <select
                    id="servicio"
                    name="servicio"
                    value={formData.servicio}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm cursor-pointer"
                  >
                    <option value="">Selecciona un servicio</option>
                    <option value="Limpieza del hogar">Limpieza del hogar</option>
                    <option value="Cuidado de personas">Cuidado de personas</option>
                    <option value="Mantenimiento">Mantenimiento</option>
                    <option value="Planchado">Planchado</option>
                    <option value="Otros">Otros servicios</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="mensaje" className="block text-sm font-semibold text-gray-900 mb-2">
                    Mensaje
                  </label>
                  <textarea
                    id="mensaje"
                    name="mensaje"
                    value={formData.mensaje}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm resize-none"
                    placeholder="Cuéntanos qué necesitas..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Máximo 500 caracteres</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-teal-500 text-white px-6 py-3.5 rounded-lg text-base font-semibold hover:bg-teal-600 transition-all transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Enviando...' : 'Solicitar presupuesto'}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start space-x-3">
                    <i className="ri-checkbox-circle-line text-xl text-green-600 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-green-900">¡Mensaje enviado!</p>
                      <p className="text-sm text-green-700">Nos pondremos en contacto contigo pronto.</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                    <i className="ri-error-warning-line text-xl text-red-600 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-red-900">Error al enviar</p>
                      <p className="text-sm text-red-700">Por favor, inténtalo de nuevo.</p>
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;